import React from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import URI from 'urijs';
import _, { compose, partial } from 'underscore';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { withSettingsConsumer } from '@bingads-webui-react/settings-context';
import { withUserConsumer } from '@bingads-webui-react/user-context';
import { perfMarkerPropTypes } from '@bingads-webui/performance-marker';

import { getAccountBreadcrumbStyles } from '../../styles';
import { CustomerMenuItem, AccountMenuItem, CustomerBreadcrumbItem } from './accounts-dropdown-menu-item';

const ACCOUNT_STATUS_TEXT_BY_ID = {
  234: _TL_('Paused'),
  32: _TL_('Inactive'),
};

const nodeRenderer = ({
  name, number, id, isCustomer, owningCustomerIdInHierarchy, uiLifecycleStatusLocId, nodeAriaLabelledby,
}) => {
  const cid = isCustomer ? id : owningCustomerIdInHierarchy;
  const aid = isCustomer ? null : id;

  const statusText = ACCOUNT_STATUS_TEXT_BY_ID[uiLifecycleStatusLocId];

  return isCustomer ?
    (
      <CustomerMenuItem
        name={name}
        number={number}
        cid={cid}
        nodeAriaLabelledby={nodeAriaLabelledby}
      />) :
    (
      <AccountMenuItem
        name={name}
        number={number}
        statusText={statusText}
        cid={cid}
        aid={aid}
        nodeAriaLabelledby={nodeAriaLabelledby}
      />
    );
};

const entityUrlUpdater = ({
  isCustomer, cid, aid, uid,
}) => {
  // use preference instead of one specific route for navigation
  if (isCustomer) {
    return new URI(`${window.location.origin}/campaign/vnext/accounts`).search({ uid, cid }).href();
  }
  return new URI(`${window.location.origin}/campaign/vnext`).search({ uid, cid, aid }).href();
};

const breadcrumbItemRenderer = (item) => {
  const itemClasses = ['breadcrumb-anchor'];

  return (
    <li className="breadcrumb-item" data-item-name={item.name} key={item.key}>
      {item.href ?
        <CustomerBreadcrumbItem classes={itemClasses} name={item.text} cid={item.key} />
            : <span className={itemClasses}>{item.text}</span>
          }
    </li>
  );
};

const AccountSelectorPopOverBase = ({
  classNames,
  AccountSelector,
  classes,
  i18n,
  settingsContext,
  userContext,
  ccCustomerService,
  selectedEntity,
  perfMarker,
  showAccountSelectorView,
  switchAccountsLabelClicked,
}) => {
  const { Urls } = settingsContext;
  let ccBaseUrl;

  const { CurrentUser, CurrentCustomer } = userContext;
  // TODO: we need this to fix local build
  // remove this once the Urls can get from local
  if (Urls) {
    const { ClientCenterBaseUrl } = Urls;
    ccBaseUrl = ClientCenterBaseUrl;
  } else {
    ccBaseUrl = 'http://localhost/cc';
  }
  let rootCustomerId;
  if (CurrentUser.IsInternal && CurrentUser.RootCustomerId) {
    rootCustomerId = CurrentUser.RootCustomerId;
  } else if (CurrentUser.IsInternal || CurrentUser.IsAggregator) {
    rootCustomerId = CurrentCustomer.Id;
  } else {
    rootCustomerId = CurrentUser.ParentCustomerId;
  }

  const customerService = ccCustomerService({ ccBaseUrl });

  return (
    <div className={classnames(classes.dropdownMenu, classNames)} >
      <AccountSelector
        ccCustomerService={customerService}
        rootCustomerId={rootCustomerId}
        selectedEntity={selectedEntity}
        nodes={[]}
        i18n={i18n}
        enableOnScrollLoad
        returnOnlyDirectlyLinkedEntities={false}
        nodeRenderer={nodeRenderer}
        updateEntityUrl={entityUrlUpdater}
        breadcrumbItemRenderer={breadcrumbItemRenderer}
        cellHeight={36}
        userId={CurrentUser.Id}
        showAccountSelectorView={showAccountSelectorView}
        switchAccountsLabelClicked={switchAccountsLabelClicked}
        useStorageForRCID={CurrentUser.IsInternal}
        ignoreFilter={false}
        perfMarker={perfMarker}
      />
    </div>
  );
};

AccountSelectorPopOverBase.propTypes = {
  classNames: PropTypes.string,
  classes: PropTypes.shape({
    dropdownMenu: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  userContext: PropTypes.shape({
    CurrentUser: PropTypes.shape.isRequired,
    CurrentCustomer: PropTypes.shape.isRequired,
  }).isRequired,
  settingsContext: PropTypes.shape({
    Urls: PropTypes.shape.isRequired,
  }).isRequired,
  ccCustomerService: PropTypes.func.isRequired,
  AccountSelector: PropTypes.elementType.isRequired,
  perfMarker: perfMarkerPropTypes,
  selectedEntity: PropTypes.shape({}).isRequired,
  showAccountSelectorView: PropTypes.bool.isRequired,
  switchAccountsLabelClicked: PropTypes.func.isRequired,
};

AccountSelectorPopOverBase.defaultProps = {
  classNames: '',
  perfMarker: null,
};

export const AccountSelectorPopOver = compose(
  partial(withDefaultStyles, _, getAccountBreadcrumbStyles),
  withUserConsumer,
  withSettingsConsumer,
  withI18nConsumer
)(AccountSelectorPopOverBase);
